/**
 * Search modal component for documentation
 */

import {useState, useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

interface SearchResult {
  title: string;
  path: string;
  description?: string;
  category?: string;
}

// Expanded search index - in production, this would come from a search index
const searchIndex: SearchResult[] = [
  {title: 'Welcome', path: '/docs', description: 'Get started with VoxaPilot', category: 'Get Started'},
  {title: 'Quickstart', path: '/docs/quickstart', description: 'Quick start guide', category: 'Get Started'},
  {title: 'Concepts', path: '/docs/concepts', description: 'Core concepts', category: 'Get Started'},
  {title: 'Models', path: '/docs/models', description: 'AI models and capabilities', category: 'Get Started'},
  {title: 'Transcription', path: '/docs/features/transcription', description: 'Real-time transcription', category: 'Features'},
  {title: 'AI Summaries', path: '/docs/features/summaries', description: 'AI-powered summaries', category: 'Features'},
  {title: 'Speaker Detection', path: '/docs/features/speaker-detection', description: 'Automatic speaker identification', category: 'Features'},
  {title: 'Analytics', path: '/docs/features/analytics', description: 'Meeting metrics and insights', category: 'Features'},
  {title: 'Zoom Integration', path: '/docs/integrations/zoom', description: 'Connect with Zoom', category: 'Integrations'},
  {title: 'Google Meet Integration', path: '/docs/integrations/google-meet', description: 'Connect with Google Meet', category: 'Integrations'},
  {title: 'Teams Integration', path: '/docs/integrations/teams', description: 'Connect with Microsoft Teams', category: 'Integrations'},
  {title: 'Slack Integration', path: '/docs/integrations/slack', description: 'Share summaries in Slack', category: 'Integrations'},
  {title: 'API Overview', path: '/docs/api/overview', description: 'API documentation', category: 'API'},
  {title: 'API Authentication', path: '/docs/api/authentication', description: 'How to authenticate with the API', category: 'API'},
  {title: 'API Endpoints', path: '/docs/api/endpoints', description: 'Complete API reference', category: 'API'},
  {title: 'Settings', path: '/docs/configuration/settings', description: 'Configure VoxaPilot', category: 'Configuration'},
];

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchModal({isOpen, onClose}: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.trim()) {
      const filtered = searchIndex.filter(
        (item) =>
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.description?.toLowerCase().includes(query.toLowerCase()) ||
          item.category?.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered.slice(0, 10));
      setSelectedIndex(0);
    } else {
      // Show recent/popular items when no query
      setResults(searchIndex.slice(0, 5));
      setSelectedIndex(0);
    }
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(results.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleResultClick(results[selectedIndex].path);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleResultClick = (path: string) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search docs..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-3 border border-gray-200 dark:border-gray-700 rounded-md text-base bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {results.length > 0 ? (
            <div className="py-2">
              {results.map((result, index) => (
                <button
                  key={result.path}
                  onClick={() => handleResultClick(result.path)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    index === selectedIndex ? 'bg-gray-50 dark:bg-gray-700' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-textPrimary dark:text-gray-100">{result.title}</div>
                      {result.description && (
                        <div className="text-sm text-textSecondary dark:text-gray-400 mt-1">{result.description}</div>
                      )}
                    </div>
                    {result.category && (
                      <span className="ml-4 px-2 py-1 text-xs font-medium text-textSecondary dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded">
                        {result.category}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : query.trim() ? (
            <div className="p-8 text-center">
              <p className="text-sm text-textSecondary dark:text-gray-400">No results found for "{query}"</p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <p className="text-xs text-textSecondary dark:text-gray-400 text-center">You can also Ask AI</p>
        </div>
      </div>
    </div>
  );
}

